'use client'

import { useEffect, useState } from 'react'
import { Message } from '@locmod/intl'

import { Icon, Logo } from 'components/ui'
import { Button } from 'components/inputs'

import messages from './messages'


interface GeoBlockerProps {
  children: React.ReactNode
}

const GeoBlocker: React.FC<GeoBlockerProps> = ({ children }) => {
  const [ isBlocked, setIsBlocked ] = useState(false)
  const [ country, setCountry ] = useState<string>('')
  const [ isLoading, setIsLoading ] = useState(true)

  useEffect(() => {
    // Detectar país usando una API externa
    const detectCountry = async () => {
      try {
        // Usar una API de geolocalización
        const response = await fetch('https://ipapi.co/json/')
        const data = await response.json()

        const detectedCountry = data.country_code
        setCountry(detectedCountry)

        console.log('🌍 Detected country:', detectedCountry)

        // Bloquear Colombia
        if (detectedCountry === 'CO') {
          console.log('🚫 Blocking Colombia')
          setIsBlocked(true)
        }
        else {
          console.log('✅ Access allowed for country:', detectedCountry)
        }
      }
      catch (error) {
        console.error('Error detecting country:', error)
        // En caso de error, permitir acceso
      }
      finally {
        setIsLoading(false)
      }
    }

    detectCountry()
  }, [])

  const handleContactSupport = () => {
    // Aquí puedes agregar lógica para contactar soporte
    window.open('mailto:support@winem.com?subject=Geo Blocking Inquiry', '_blank')
  }

  const handleGoBack = () => {
    window.history.back()
  }

  // Mostrar loading mientras detecta el país
  if (isLoading) {
    return (
      <div className="fixed inset-0 bg-bg-l0 flex items-center justify-center z-50">
        <div className="text-center">
          {/* Logo con animación */}
          <div className="mb-8">
            <div className="w-20 h-20 mx-auto bg-brand-5 rounded-full flex items-center justify-center mb-4 animate-pulse">
              <div className="flex items-center justify-center w-full h-full">
                <Logo className="w-10 h-10" />
              </div>
            </div>
          </div>

          {/* Spinner de carga */}
          <div className="w-8 h-8 border-2 border-brand-50 border-t-transparent rounded-full animate-spin mx-auto mb-4" />

          {/* Texto de carga */}
          <p className="text-grey-70 text-sm">Verificando ubicación...</p>
        </div>
      </div>
    )
  }

  // Si está bloqueado, reemplazar TODO el contenido de la aplicación
  if (isBlocked) {
    return (
      <div className="min-h-screen bg-bg-l0 flex items-center justify-center px-4">
        <div className="max-w-md w-full text-center">
          {/* Logo del proyecto */}
          <div className="mb-8">
            <div className="w-24 h-24 mx-auto bg-brand-5 rounded-full flex items-center justify-center mb-4">
              <div className="flex items-center justify-center w-full h-full">
                <Logo className="w-12 h-12" />
              </div>
            </div>
          </div>

          {/* Título principal */}
          <h1 className="text-heading-h1 font-bold text-grey-90 mb-4">
            <Message value={messages.title} />
          </h1>

          {/* Mensaje de bloqueo */}
          <div className="bg-bg-l1 border border-grey-15 rounded-lg p-6 mb-8">
            <Message
              className="text-caption-14 text-grey-70 mb-4"
              value={
                {
                  en: 'We apologize, but our services are not available in Colombia due to local regulations and compliance requirements.',
                  es: 'Lamentamos informar que nuestros servicios no están disponibles en Colombia debido a regulaciones locales y requisitos de cumplimiento.',
                }
              }
            />

            <Message
              className="text-caption-13 text-grey-60"
              value={messages.reason}
            />
          </div>

          {/* Información adicional */}
          <div className="space-y-4 mb-8">
            <div className="text-caption-12 text-grey-40">
              <Message
                value={
                  {
                    en: 'Detected location: Colombia (CO)',
                    es: 'Ubicación detectada: Colombia (CO)',
                  }
                }
              />
            </div>
          </div>

          {/* Footer */}
          <div className="mt-8 pt-6 border-t border-grey-15">
            <Message className="text-caption-12 text-grey-40" value={messages.footer} />
          </div>
        </div>
      </div>
    )
  }

  // Si no está bloqueado, mostrar la aplicación
  return <>{children}</>
}

export default GeoBlocker
